import { Link, useNavigate } from 'react-router-dom'
import { formatAmount, formatDayTime } from '@dorixona/utils'
import { Icon } from '../../ui/Icon'
import { Failed } from '../../ui/Load'
import { TableSkeleton } from '../../ui/Skeleton'
import { useStores } from '../stores/api'
import { useProductStock, useProducts, type Product } from './api'
import { stockRows } from './model'

export interface StockTabProps {
  item: Product
  /** Роль видит раздел «Аптеки»: тогда в таблице вся сеть с адресами. */
  withStores: boolean
}

/**
 * Остатки по аптекам и синхронизация с FOM. Остатки приходят из учётной
 * системы и здесь не правятся. Кнопки «Синхронизировать сейчас» здесь нет:
 * повторное применение файлов (BR-47) не работало, карточка только сообщает.
 */
export function StockTab({ item, withStores }: StockTabProps) {
  const navigate = useNavigate()
  const stock = useProductStock(item.id, true)
  const stores = useStores({ enabled: withStores })
  const catalog = useProducts({ page: 1, pageSize: 1 })
  const rows = stock.data ? stockRows(stock.data, stores.data) : []
  const total = rows.reduce((sum, r) => sum + r.qty, 0)

  return (
    <div className="ui-detail-grid">
      <section className="ui-table-card" aria-labelledby="pd-stock">
        <div className="ui-card-head">
          <h3 id="pd-stock">
            Остатки по аптекам <span className="ui-tag">FOM</span>
          </h3>
          <span className="aside">самовывоз — где товар есть</span>
        </div>
        {stock.isPending ? <TableSkeleton what="остатки" /> : null}
        {stock.isError ? (
          <Failed
            what="остатки"
            error={stock.error}
            onRetry={() => {
              void stock.refetch()
            }}
          />
        ) : null}
        {stock.data && rows.length === 0 ? (
          <div className="ui-empty">
            <Icon name="i-pin" />
            <b>Товара нет ни в одной аптеке</b>
          </div>
        ) : null}
        {rows.length > 0 ? (
          <div className="ui-table-wrap">
            <table className="ui-table">
              <thead>
                <tr>
                  <th scope="col">Аптека</th>
                  <th scope="col">Адрес</th>
                  <th scope="col" data-align="end">
                    Остаток, шт
                  </th>
                  <th scope="col" data-align="end">
                    Цена, сум
                  </th>
                  <th scope="col">Самовывоз</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const href = `/stores/${row.storeId}`
                  return (
                    <tr
                      key={row.storeId}
                      data-href={href}
                      onClick={() => {
                        void navigate(href)
                      }}
                    >
                      <td>
                        <Link className="primary" to={href}>
                          {row.name}
                        </Link>
                      </td>
                      <td>
                        <span className="secondary">{row.place}</span>
                      </td>
                      <td className="num" data-align="end">
                        {formatAmount(row.qty)}
                      </td>
                      <td className="num" data-align="end">
                        {row.price ? formatAmount(row.price) : '—'}
                      </td>
                      <td>
                        {!row.active ? (
                          <span className="ui-status">аптека закрыта</span>
                        ) : row.qty > 0 ? (
                          <span className="ui-status" data-tone="success">
                            доступен
                          </span>
                        ) : (
                          <span className="ui-status">недоступен</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot>
                <tr className="pd-total">
                  <th scope="row" colSpan={2}>
                    Итого по сети
                  </th>
                  <td className="num" data-align="end">
                    {formatAmount(total)}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        ) : null}
      </section>

      <section className="ui-card" aria-labelledby="pd-sync">
        <div className="ui-card-head">
          <h3 id="pd-sync">Синхронизация</h3>
        </div>
        <div className="ui-card-body pd-facts-body">
          <div className="ui-facts">
            <div>
              <span className="k">Источник</span>
              <span className="v">FOM</span>
            </div>
            <div>
              <span className="k">Последний обмен</span>
              <span className="v num">
                {item.syncedAt ? formatDayTime(item.syncedAt) : 'ещё не было'}
              </span>
            </div>
            <div>
              <span className="k">Позиций в каталоге</span>
              <span className="v num">{catalog.data ? formatAmount(catalog.data.total) : '—'}</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
